<template>
    <div>
        <div class="card pb-card">
            <div class="pb-header">
                <TitleButton btnTitle="List" :showBtn="true" :showAddNew="false" title="Add Purchase Bill"
                    @onClickCloseButton="navigateToListPage" />
            </div>
            <div class="pb-body">
                <section class="pb-section">
                    <div class="row g-2">
                        <div class="col-12 col-sm-6 col-lg-4">
                            <div class="d-flex align-items-start justify-content-between">
                                <label class="pb-label">Supplier <span class="text-danger">*</span></label>
                                <span v-if="route.query.type !== 'purchase-order-bill'" class="pb-search-icon" @click="onOpenContactSearchModal">
                                    <i class="fas fa-search"></i>
                                </span>
                            </div>
                            <AsyncSelect
                                :disabled="route.query.type === 'purchase-order-bill'"
                                placeholder="Select Supplier"
                                v-model="contactProfile"
                                :api-service="fetchContactProfiles"
                                :format-label="formatPatientLabel"
                                :additional-query="{type: 'supplier'}"
                            />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="pb-label" for="bill-date">Date <span class="text-danger">*</span></label>
                            <input id="bill-date" class="form-control" tabindex="1" type="date" v-model="formData.date">
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="pb-label">Warehouse Location <span class="text-danger">*</span></label>
                            <v-select placeholder="Select Warehouse Location" v-model="formData.location_id"
                                :options="locations" label="text" :reduce="text => text.id" />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="pb-label">Account Head <span class="text-danger">*</span></label>
                            <v-select placeholder="Select Account Head" v-model="formData.account_head_id" :options="accountPayable"
                                label="name" :reduce="name => name.id" />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="pb-label">Business</label>
                            <v-select placeholder="Select Business" v-model="formData.business_id" :options="business"
                                label="name" :reduce="name => name.id" />
                        </div>
                    </div>
                </section>
                <section class="pb-section">
                    <ProductForm @handleProductAdd="handleProductAdd" ref="productForm"/>
                    <ProductsTable :itemDetails="formData.item_details" @removeBill="removeBill" :formRef="productForm" />
                </section>
                <div class="row g-2">
                    <div class="col-12 col-lg-7">
                        <section class="pb-section pb-section--fill pb-memo">
                            <label class="pb-label" for="description">Memo</label>
                            <vField as="textarea" name="description" id="description" rows="5" class="form-control"
                                placeholder="Write a memo" v-model="formData.description" />
                        </section>
                    </div>
                    <div class="col-12 col-lg-5">
                        <section class="pb-section pb-section--fill">
                            <div class="pb-summary-row">
                                <span>Sub Total</span>
                                <span class="pb-summary-value">{{ formatAmount(subTotal) }}</span>
                            </div>
                            <div class="pb-summary-row">
                                <span>VAT/GST</span>
                                <span class="pb-summary-value">{{ formatAmount(totalVAT) }}</span>
                            </div>
                            <div class="pb-summary-row">
                                <span>Total Amount</span>
                                <span class="pb-summary-value">{{ formatAmount(total) }}</span>
                            </div>
                            <div class="pb-summary-adjust">
                                <label class="pb-label" for="adjustment-amount">Adjustment Amount</label>
                                <input id="adjustment-amount" v-model.trim="discountAmount" type="number"
                                    class="form-control text-end" placeholder="0.00" :min="0" @keyup="updateGrandTotal">
                            </div>
                            <div v-if="discountAmount > 0" class="pb-summary-adjust">
                                <label class="pb-label">Settlement Account</label>
                                <v-select placeholder="Select Discount Head" v-model="formData.discount_head_id"
                                    :options="salesAccountHeads" label="name" :reduce="name => name.id" />
                            </div>
                            <div class="pb-summary-net">
                                <label for="net-bill">Net Bill</label>
                                <input id="net-bill" v-model.trim="grandTotal" type="number"
                                    class="form-control text-end pb-net-input" :min="0" @keyup="updateDiscount">
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <div class="pb-footer">
                <button :disabled="productLoader" @click="navigateToListPage"
                    class="btn btn-outline-secondary">Cancel</button>
                <button :disabled="saveButtonLoader || saveNewButtonLoader" @click="handleSubmit(false)"
                    class="btn btn-outline-primary">Save &amp; New</button>
                <button :disabled="saveButtonLoader || saveNewButtonLoader" @click="handleSubmit(true)"
                    class="btn btn-primary">Save</button>
            </div>
        </div>
        <GlobalLoader />
        <ContactDropdownFilter v-if="store.state.isModalOpenThree" @onSearchContact="onSearchContact" type="supplier"
            :companyRoles="companyRoles" />
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import handleCBusinesses from '@/services/modules/businesses'
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handleContact from '@/services/modules/contact'
import TitleButton from '@/components/atom/TitleButton'

import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import { useStore } from "vuex";
import ContactDropdownFilter from "@/components/atom/Contact/ContactDropdownFilter.vue";
import handleRole from "@/services/modules/role";
import ProductForm from "./ProductForm.vue"
import ProductsTable from "./ProductsTable.vue"
import useDate from '@/services/utils/day'
import handleBarcode from "@/services/modules/barcode";
import pdfPrinter from '@/services/utils/pbPdfPrinter';
import handleCompany from "@/services/modules/company";
import handleRequisitions from "@/services/modules/procurement/requisition";
import figureFormatter from "@/services/utils/figureFormatter";

const router = useRouter()
const route = useRoute()
const store = useStore()

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchSinglePO } = handleRequisitions();
const { fetchAccountHead, ...rest } = handlePurchase()
const { fetchBusinessList } = handleCBusinesses()
const { fetchBusinessLocationsList } = handleBusinessesLocations()
const { fetchContactProfiles } = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchCompanyDefaultRoles } = handleRole();
const { fetchBarcode } = handleBarcode();
const { formatDate, currentDate } = useDate()
const { exportToPDF } = pdfPrinter();
const {fetchCompanyInfo} = handleCompany();
const { commaFormat } = figureFormatter();

const companyId = computed(() => {
    return route.params.companyId
})
const loader = ref(false)
const productLoader = ref(false)
const saveButtonLoader = ref(false)
const saveNewButtonLoader = ref(false)
const accountHeads = ref([])
const business = ref([])
const locations = ref([])
const salesAccountHeads = ref([])
const accountPayable = ref([])
const companyInfo = ref({});
const discountAmount = ref(null);
const contactProfile = ref(null);
const productForm = ref(null);
const companyRoles = ref([]);
const grandTotal = ref(0);
const formData = ref({
    company_id: companyId,
    contact_profile_id: null,
    account_head_id: null,
    business_id: null,
    location_id: null,
    bill_number: '',
    status: 'active',
    date: '',
    has_item_detail: true,
    description: '',
    purchase_order_master_id: null,
    account_details: [],
    item_details: [],
})

onMounted(async () => {
    loader.value = true
    formData.value.date = currentDate();
    const companyQuery = `?company_id=${companyId.value}`

    const accountHeadRes = fetchAccountHead(companyQuery)
    const businessRes = fetchBusinessList(companyQuery)
    const businessLocationRes = fetchBusinessLocationsList(companyQuery)
    const payableRes = rest.fetchAccountPayable(companyQuery)
    const salesAccountHeadsRes = rest.getAccountHeadBySlag("discount_receipt", companyQuery)
    Promise.all([
        accountHeadRes.then(res => {
            if (res.data) accountHeads.value = res.data
        }),
        businessRes.then(res => {
            if (res.data) business.value = res.data
        }),
        businessLocationRes.then(res => {
            if (res.data) locations.value = res.data
        }),
        fetchCompanyDefaultRoles(companyQuery).then(res => {
            if (res.data) companyRoles.value = res.data
        }),
        payableRes.then(res => {
            if (res.data) {
                accountPayable.value = res.data
            }
            if (res.data?.length) {
                formData.value.account_head_id = res.data[0].id
            }
        }),
        salesAccountHeadsRes.then(res => {
            if (res.data) salesAccountHeads.value = res.data
            formData.value.discount_head_id = res.data[0] ? res.data[0].id : null
        }), 
        fetchCompanyInfo(companyId.value).then(res => {
            if(res){
                companyInfo.value = res.data
            }
        })
    ])
        .then(() => { loader.value = false })
        .catch(() => { loader.value = false })

    let poId = route.query.poId;
    if (! poId || (route.query.type !== 'purchase-order-bill')) {
      return;
    }

    formData.value.purchase_order_master_id = poId;

    await fetchSinglePO(poId, companyQuery).then((res) => {
      if (! res.status) {
        return;
      }

      contactProfile.value = {
        id: res.data.contact_profile.id,
        name: res.data.contact_profile.full_name
      }

      formatPurchaseItem(res.data.purchase_general)
    }).catch((err) => {
      console.log(err);
    });

})

function formatPurchaseItem(poItems) {
  for (let po of poItems) {
    formData.value.item_details.push({
      product_id: po.product_id,
      product_name: po.product.name,
      batch_number: '',
      mfn_date: '',
      exp_date: '',
      selling_rate: '',
      quantity: po.quantity,
      rate: po.rate,
      discount_percent: po.discount_amount,
      vat: po.vat,
      vat_amount: po.vat_amount,
      tax_rate: null,
      tax_rate_id: null,
      g_total: po.total_amount,
      description: po.description,
      product: {
        id:po.product_id,
        text:po.product.name
      }
    });
  }
}

const isItem = computed(() => {
    return formData.value.has_item_detail
})

const formatAmount = (value) => commaFormat(Number(value || 0).toFixed(2))

const subTotal = computed(() => {
    let subTotal = 0
    if (formData.value.has_item_detail) {
        formData.value.item_details.map(item => {
            subTotal += parseFloat((item.quantity * item.rate) - (item.discount_amount ?? 0))
        })
    }
    if (!formData.value.has_item_detail) {
        formData.value.account_details.map(item => {
            if (item.amount) { subTotal += parseFloat(item.amount) }
        })
    }

    return subTotal.toFixed(4)
})

const totalVAT = computed(() => {
    let vat = 0
    if (formData.value.has_item_detail) {
        formData.value.item_details.map(item => {
            if (item.vat_amount) {
                vat += parseFloat(item.vat_amount)
            }
        })
    }
    if (!formData.value.has_item_detail) {
        formData.value.account_details.map(item => {
            if (item.vat_amount) {
                vat += parseFloat(item.vat_amount)
            }
        })
    }

    return vat.toFixed(4)
})

const total = computed(() => {
    return parseFloat(subTotal.value) + parseFloat(totalVAT.value)
})

const updateGrandTotal = () => {
    grandTotal.value = total.value - parseFloat(discountAmount.value);
}

const updateDiscount = () => {
    discountAmount.value = (total.value - grandTotal.value).toFixed(4);
}

watch(discountAmount, () => {
    formData.value.adjustment_amount = parseFloat(discountAmount.value)
})

watch(total, () => {
    if (discountAmount.value && discountAmount.value !== "") {
        grandTotal.value = total.value - parseFloat(discountAmount.value);
        return;
    }
    grandTotal.value = total.value;
})

watch(contactProfile, (newVal) => {
    if (!newVal) {
        formData.value.contact_profile_id = null;
        return;
    }

    formData.value.contact_profile_id = newVal.id;
})

function navigateToListPage() {
    delete route.query.poId;
    router.push({
      name: (route.query.type === 'purchase-order-bill')
                     ? 'purchase-order-bill'
                     : 'purchase-bill-manager',
      params: route.params, query: route.query
    })
}
function onOpenContactSearchModal() {
    store.state.isModalOpenThree = true;
}

function onSearchContact(contact) {
    contactProfile.value = contact
}

function removeBill(index) {
    formData.value.item_details.splice(index, 1)
}

const goToPrint = (id) => {
    const user = store.state.user.user
    const companyQuery = `?company_id=${route.params.companyId}`;
    let barcodeQuery = "";
    let qrcode = null
    let barcode = null
    let purchaseBill = null

    rest.fetchPurchaseBill(id, companyQuery).then( (res) =>{
        if( res ){
            purchaseBill = res.data;
            let qrcodeData = JSON.stringify({
                ref_no: purchaseBill.bill_number,
                ref_date: purchaseBill.date,
                created_by: user.name,
                created_date_time: formatDate(new Date())
            })
            barcodeQuery = `?code=${purchaseBill.bill_number}&qrcode=${qrcodeData}`;     
        }}).then (() => {
            fetchBarcode(barcodeQuery).then( (res) => {
            barcode = res.barcode;
            qrcode = res.qrcode
        }).then( () => {
            exportToPDF(companyInfo.value, purchaseBill, barcode, qrcode, user.name, 1)
        })  
    }).catch( (err) => {
        console.log(err);
    })
}

function handleSubmit(redirect = false) {

    saveNewButtonLoader.value = true
        
    if (!formData.value.date) formData.value.date = currentDate();

    if (! formData.value.adjustment_amount) {
        formData.value.discount_head_id = null;
    }

    let copyFormData = JSON.parse(JSON.stringify(formData.value))
    copyFormData.has_item_detail = formData.value.has_item_detail ? 1 : 0
    copyFormData.updateSalePrice = 1;
    copyFormData.voucher_type = "journal_voucher"
    copyFormData.txn_type = "bill_journal"

    if (isItem.value) {
        delete copyFormData.account_details
        copyFormData.item_details = formData.value.item_details
    } else {
        delete copyFormData.item_details
        copyFormData.account_details = formData.value.account_details
    }
    rest.storeBill(copyFormData)
        .then(res => {
            saveButtonLoader.value = false
            saveNewButtonLoader.value = false
            if (res.status) {
                showSuccess(res.message)
                if (redirect) navigateToListPage()
                if(res.data && res.data.id) {
                    goToPrint(res.data.id);
                }
                resetForm()
            }
            if (!res.status) {
                showError(res.message)
            }
        })
        .catch(() => {
            saveButtonLoader.value = false
            saveNewButtonLoader.value = false
            productLoader.value = false
        })
}

// have to refactor
async function resetForm() {
    formData.value.item_details = []
    formData.value.description = '';
    contactProfile.value = null;
    discountAmount.value = null;
}

const handleProductAdd = (item, index) => {
    if(index !== null && Array.isArray(formData.value.item_details) && !!formData.value.item_details[index]) {
        formData.value.item_details[index] = item;
        return;
    }

    formData.value.item_details.push(item);
}

</script>

<style scoped src="./purchase-bill.css"></style>
